import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { getTickets, getTicketsStatus } from '@services/ticketService'
import api from '@services/api'
import { useAuth } from './AuthContext'

const WalletContext = createContext(null)

export const WalletProvider = ({ children }) => {
  const { isAuthenticated } = useAuth()
  
  const [upcomingTickets, setUpcomingTickets] = useState([])
  const [upcomingTotalPages, setUpcomingTotalPages] = useState(1)
  const [upcomingLoading, setUpcomingLoading] = useState(false)

  const [historyTickets, setHistoryTickets] = useState([])
  const [historyTotalPages, setHistoryTotalPages] = useState(1)
  const [historyLoading, setHistoryLoading] = useState(false)

  const [sales, setSales] = useState([])
  const [salesTotalPages, setSalesTotalPages] = useState(1)
  const [salesLoading, setSalesLoading] = useState(false)

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const isRefreshingStatusesRef = useRef(false)

  // Reset wallet state when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setUpcomingTickets([])
      setUpcomingTotalPages(1)
      setUpcomingLoading(false)
      setHistoryTickets([])
      setHistoryTotalPages(1)
      setHistoryLoading(false)
      setSales([])
      setSalesTotalPages(1)
      setSalesLoading(false)
      setHasLoadedOnce(false)
    }
  }, [isAuthenticated])

  const fetchUpcoming = useCallback(async (page = 1) => {
    if (!isAuthenticated) return
    setUpcomingLoading(true)
    try {
      const response = await getTickets(page, 10, 'RESERVED,ISSUED')
      setUpcomingTickets(response?.items || [])
      setUpcomingTotalPages(response?.total_pages || 1)
    } catch (err) {
      console.error('Error fetching upcoming tickets:', err)
      setUpcomingTickets([])
    } finally {
      setUpcomingLoading(false)
    }
  }, [isAuthenticated])

  const fetchHistory = useCallback(async (page = 1) => {
    if (!isAuthenticated) return
    setHistoryLoading(true)
    try {
      const response = await getTickets(page, 10, 'USED,REFUNDED,NOSHOW')
      setHistoryTickets(response?.items || [])
      setHistoryTotalPages(response?.total_pages || 1)
    } catch (err) {
      console.error('Error fetching history tickets:', err)
      setHistoryTickets([])
    } finally {
      setHistoryLoading(false)
    }
  }, [isAuthenticated])

  const fetchSales = useCallback(async (page = 1) => {
    if (!isAuthenticated) return
    setSalesLoading(true)
    try {
      const response = await api.get(`/sales/?page=${page}&page_size=10`)
      setSales(response?.items || [])
      setSalesTotalPages(response?.total_pages || 1)
    } catch (err) {
      console.error('Error fetching sales:', err)
      setSales([])
    } finally {
      setSalesLoading(false)
    }
  }, [isAuthenticated])

  const loadAll = useCallback(async (force = false) => {
    if (!isAuthenticated) return
    if (hasLoadedOnce && !force) return
    
    // Carrega exclusivamente os ingressos futuros ativos no carregamento inicial
    try {
      await fetchUpcoming(1)
    } finally {
      setHasLoadedOnce(true)
    }
  }, [isAuthenticated, hasLoadedOnce, fetchUpcoming])

  // Helper to sync statuses from lighter response
  const _syncTicketStatuses = useCallback((currentTransactions, statusItems) => {
    if (!currentTransactions?.length || !statusItems?.length) return currentTransactions;

    // Map of ticket_id -> new status
    const statusMap = statusItems.reduce((acc, trans) => {
      if (trans && trans.tickets && Array.isArray(trans.tickets)) {
        trans.tickets.forEach(t => {
          if (t && t.ticket_id) {
            acc[t.ticket_id] = t.status;
          }
        });
      }
      return acc;
    }, {});

    let hasChanges = false;
    const updatedTransactions = currentTransactions.map(trans => {
      let transChanged = false;
      const updatedTickets = trans.tickets.map(t => {
        const newStatus = statusMap[t.ticket_id];
        if (newStatus && newStatus !== t.status) {
          transChanged = true;
          hasChanges = true;
          return { ...t, status: newStatus };
        }
        return t;
      });

      return transChanged ? { ...trans, tickets: updatedTickets } : trans;
    });

    return hasChanges ? updatedTransactions : currentTransactions;
  }, []);

  const refreshStatuses = useCallback(async (targetOrUpcomingPage = 'tickets', maybeUpcomingPage = 1, maybeHistoryPage = 1, maybeSalesPage = 1) => {
    if (!isAuthenticated) return;
    if (isRefreshingStatusesRef.current) return;
    isRefreshingStatusesRef.current = true;

    let target = 'tickets';
    let upcomingPage = 1;
    let historyPage = 1;
    let salesPage = 1;

    if (typeof targetOrUpcomingPage === 'string') {
      target = targetOrUpcomingPage;
      upcomingPage = maybeUpcomingPage || 1;
      historyPage = maybeHistoryPage || 1;
      salesPage = maybeSalesPage || 1;
    } else {
      upcomingPage = targetOrUpcomingPage || 1;
      historyPage = maybeUpcomingPage || 1;
      salesPage = maybeHistoryPage || 1;
      target = 'all';
    }

    try {
      if (target === 'tickets' || target === 'all') {
        const upcomingStatus = await getTicketsStatus(upcomingPage, 10, 'RESERVED,ISSUED');
        if (upcomingStatus?.items) {
          setUpcomingTickets(prev => _syncTicketStatuses(prev, upcomingStatus.items));
        }
      }

      if (target === 'history' || target === 'all') {
        const historyStatus = await getTicketsStatus(historyPage, 10, 'USED,REFUNDED,NOSHOW');
        if (historyStatus?.items) {
          setHistoryTickets(prev => _syncTicketStatuses(prev, historyStatus.items));
        }
      }


      if (target === 'balance' || target === 'sales' || target === 'all') {
        const salesStatus = await api.get(`/sales/?page=${salesPage}&page_size=10&status_only=true`);
        if (salesStatus?.items) {
          setSales(prev => {
            if (!prev?.length) return prev;
            
            const statusMap = salesStatus.items.reduce((acc, s) => {
              if (s && s.transaction_id) {
                acc[s.transaction_id] = s.status;
              }
              return acc;
            }, {});

            let hasChanges = false;
            const updated = prev.map(s => {
              if (!s || !s.transaction_id) return s;
              const newStatus = statusMap[s.transaction_id];
              if (newStatus && newStatus !== s.status) {
                hasChanges = true;
                return { ...s, status: newStatus };
              }
              return s;
            });
            return hasChanges ? updated : prev;
          });
        }
      }
    } catch (err) {
      console.error('Error refreshing statuses:', err);
    } finally {
      isRefreshingStatusesRef.current = false;
    }
  }, [isAuthenticated, _syncTicketStatuses]);



  const value = useMemo(() => ({
    upcomingTickets,
    upcomingTotalPages,
    upcomingLoading,
    historyTickets,
    historyTotalPages,
    historyLoading,
    sales,
    salesTotalPages,
    salesLoading,
    hasLoadedOnce,
    fetchUpcoming,
    fetchHistory,
    fetchSales,
    loadAll,
    refreshStatuses,
    setUpcomingTickets,
    setHistoryTickets,
    setSales
  }), [
    upcomingTickets, upcomingTotalPages, upcomingLoading,
    historyTickets, historyTotalPages, historyLoading,
    sales, salesTotalPages, salesLoading,
    hasLoadedOnce, fetchUpcoming, fetchHistory, fetchSales, loadAll, refreshStatuses
  ])

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  )
}

export const useWallet = () => {
  const context = useContext(WalletContext)
  if (!context) throw new Error('useWallet deve ser usado dentro de WalletProvider')
  return context
}
